import { useEffect, useRef } from 'react';

import type { UnlayerUserIdentity } from '../model/types';

const UNLAYER_SCRIPT_URL = 'https://editor.unlayer.com/embed.js';
const EDITOR_CONTAINER_ID = 'email-composer-editor';

interface UnlayerEditorApi {
  init: (options: {
    id: string;
    displayMode?: string;
    projectId?: number;
    features?: {
      ai?: boolean;
    };
    user?: {
      id: string;
      signature: string;
      name?: string;
      email?: string;
    };
  }) => void;
  loadDesign: (design: Record<string, unknown>) => void;
  loadBlank: (options?: { backgroundColor?: string }) => void;
  saveDesign: (cb: (design: Record<string, unknown>) => void) => void;
  exportHtml: (
    cb: (data: { design: Record<string, unknown>; html: string }) => void,
  ) => void;
  addEventListener: (event: string, cb: () => void) => void;
}

declare global {
  interface Window {
    unlayer?: UnlayerEditorApi;
  }
}

const loadUnlayerScript = (): Promise<void> => {
  if (window.unlayer) {
    return Promise.resolve();
  }

  const existingScript = document.querySelector<HTMLScriptElement>(
    `script[src="${UNLAYER_SCRIPT_URL}"]`,
  );

  if (existingScript) {
    return new Promise((resolve, reject) => {
      existingScript.addEventListener(
        'load',
        () => {
          resolve();
        },
        { once: true },
      );
      existingScript.addEventListener(
        'error',
        () => {
          reject(new Error('Failed to load Unlayer script'));
        },
        { once: true },
      );
    });
  }

  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = UNLAYER_SCRIPT_URL;
    script.async = true;
    script.onload = () => {
      resolve();
    };
    script.onerror = () => {
      reject(new Error('Failed to load Unlayer script'));
    };
    document.body.appendChild(script);
  });
};

interface UnlayerEditorProps {
  designJson: Record<string, unknown> | null;
  onReady?: () => void;
  editorRef: React.MutableRefObject<UnlayerEditorHandle | null>;
  readOnly?: boolean;
  userIdentity: UnlayerUserIdentity;
}

export interface UnlayerEditorHandle {
  saveDesign: () => Promise<Record<string, unknown>>;
  exportHtml: () => Promise<{
    design: Record<string, unknown>;
    html: string;
  }>;
  loadDesign: (design: Record<string, unknown>) => void;
  loadBlank: () => void;
}

export const UnlayerEditor = ({
  designJson,
  onReady,
  editorRef,
  readOnly = false,
  userIdentity,
}: UnlayerEditorProps) => {
  const readyRef = useRef(false);
  const pendingBlankRef = useRef(false);
  const pendingDesignRef = useRef(designJson);

  const applyBlankCanvas = () => {
    window.unlayer?.loadBlank({});
  };

  useEffect(() => {
    pendingDesignRef.current = designJson;
    pendingBlankRef.current = designJson === null;
    if (readyRef.current && window.unlayer) {
      if (designJson === null) {
        applyBlankCanvas();
      } else if (designJson) {
        pendingBlankRef.current = false;
        window.unlayer.loadDesign(designJson);
      }
    }
  }, [designJson]);

  useEffect(() => {
    let cancelled = false;

    const initEditor = async () => {
      try {
        await loadUnlayerScript();
        if (cancelled || !window.unlayer) {
          return;
        }

        const projectId = Number(import.meta.env.VITE_UNLAYER_PROJECT_ID);
        if (!Number.isFinite(projectId) || projectId <= 0) {
          throw new Error(
            'VITE_UNLAYER_PROJECT_ID is not configured for this environment.',
          );
        }

        window.unlayer.init({
          id: EDITOR_CONTAINER_ID,
          displayMode: 'email',
          projectId,
          features: {
            ai: false,
          },
          user: {
            id: userIdentity.id,
            signature: userIdentity.signature,
            ...(userIdentity.name ? { name: userIdentity.name } : {}),
            ...(userIdentity.email ? { email: userIdentity.email } : {}),
          },
        });

        window.unlayer.addEventListener('editor:ready', () => {
          readyRef.current = true;
          if (pendingBlankRef.current || pendingDesignRef.current === null) {
            applyBlankCanvas();
            pendingBlankRef.current = false;
          } else if (pendingDesignRef.current) {
            window.unlayer?.loadDesign(pendingDesignRef.current);
          }
          onReady?.();
        });

        editorRef.current = {
          saveDesign: () =>
            new Promise((resolve, reject) => {
              if (!window.unlayer) {
                reject(new Error('Unlayer not ready'));
                return;
              }
              window.unlayer.saveDesign((design) => {
                resolve(design);
              });
            }),
          exportHtml: () =>
            new Promise((resolve, reject) => {
              // Before 'editor:ready' the canvas is still blank, so an export
              // would return an empty design and overwrite saved content.
              if (!window.unlayer || !readyRef.current) {
                reject(
                  new Error(
                    'Email editor is still loading. Try again in a moment.',
                  ),
                );
                return;
              }
              window.unlayer.exportHtml((data) => {
                resolve(data);
              });
            }),
          loadDesign: (design) => {
            pendingBlankRef.current = false;
            window.unlayer?.loadDesign(design);
          },
          loadBlank: () => {
            pendingBlankRef.current = true;
            pendingDesignRef.current = null;
            if (readyRef.current && window.unlayer) {
              applyBlankCanvas();
              pendingBlankRef.current = false;
            }
          },
        };
      } catch (error) {
        console.error(error);
      }
    };

    void initEditor();

    return () => {
      cancelled = true;
      editorRef.current = null;
    };
  }, [editorRef, onReady, userIdentity]);

  return (
    <div className='relative min-h-0 flex-1'>
      <div
        id={EDITOR_CONTAINER_ID}
        className='h-full min-h-0 border border-border'
      />
      {readOnly ? (
        <div
          className='absolute inset-0 z-10 cursor-not-allowed bg-background/10'
          aria-hidden
        />
      ) : null}
    </div>
  );
};
